import { Text as T } from '@eo-locale/preact';
import ThumbsDownIcon from 'images/icons/ThumbsDown.svg';
import ThumbsUpIcon from 'images/icons/ThumbsUp.svg';
import { useCallback, useState } from 'preact/hooks';
import { Review } from 'types/review';
import { ExtendedReviewsWidgetSettings } from 'types/settings';
import { formatDate } from 'utilities/content';
import { useLocale } from 'utilities/use-locale';
import { useVoting } from 'utilities/use-voting';

import { Card } from 'components/Card';
import { ReactionButton } from 'components/ReactionButton';
import { Header } from 'components/ReviewCard/components/Header';
import { Thumbnails } from 'components/ReviewCard/components/Thumbnails';
import { useSettings } from 'components/SettingsProvider';
import { Stack } from 'components/Stack';
import { Text } from 'components/Text';

interface ReviewCardProps {
  review: Review;
}

export function ReviewCard({ review }: ReviewCardProps) {
  const {
    id,
    title,
    author,
    created_at,
    location,
    rating,
    body,
    images,
    verified_purchase,
    pros,
    cons,
    reply,
    replied_at,
    vote_up,
    source,
    vote_down,
  } = review;
  const {
    settings,
    enable_voting,
    show_location,
    utils: { shopName, geolocation },
  } = useSettings<ExtendedReviewsWidgetSettings>();
  const [votes, setVotes] = useState([vote_up, vote_down]);
  const { voteUp, voteDown, isVoting } = useVoting(id, geolocation.client_id);
  const { locale } = useLocale();

  const customDateFormat = settings.reviews.tweaks.custom_date_format_json;

  const handleVote = useCallback(async (vote: typeof voteUp) => {
    const votingRes = await vote();

    setVotes(votingRes);
  }, []);

  const prosMarkup = pros ? (
    <Stack
      spacing="extraTight"
      wrap={false}
      alignment="baseline"
      classNames={['wbtxReviewCard__pros']}
    >
      <Text
        size="small"
        variation="strong"
        as="span"
        classNames={['wbtxReviewCard__pros-label']}
      >
        <T id="review_pros_label" defaultMessage="Pros" />:
      </Text>
      <Stack.Item fill>
        <Text size="small" classNames={['wbtxReviewCard__pros-content']}>
          {pros}
        </Text>
      </Stack.Item>
    </Stack>
  ) : null;

  const consMarkup = cons ? (
    <Stack
      spacing="extraTight"
      alignment="baseline"
      classNames={['wbtxReviewCard__cons']}
    >
      <Text
        size="small"
        variation="strong"
        as="span"
        classNames={['wbtxReviewCard__cons-label']}
      >
        <T id="review_cons_label" defaultMessage="Cons" />:
      </Text>
      <Stack.Item fill>
        <Text size="small" classNames={['wbtxReviewCard__cons-content']}>
          {cons}
        </Text>
      </Stack.Item>
    </Stack>
  ) : null;

  const imagesMarkup =
    images.length > 0 ? (
      <Thumbnails items={images.map(({ image_key }) => image_key)} />
    ) : null;

  const replyMarkup =
    reply !== null && replied_at !== null ? (
      <Stack vertical spacing="tight" classNames={['wbtxReviewCard__reply']}>
        <Text
          size="extraSmall"
          as="span"
          classNames={['wbtxReviewCard__reply-title']}
        >
          <T
            id="review_replied_label"
            defaultMessage="{shop_name} replied • {date}:"
            shop_name={shopName}
            date={formatDate(replied_at, locale)}
          />
        </Text>
        <Text size="small" classNames={['wbtxReviewCard__reply-content']}>
          {reply}
        </Text>
      </Stack>
    ) : null;

  const votingMarkup = enable_voting && (
    <Stack
      distribution="trailing"
      spacing="tight"
      classNames={['ReviewCard_voting']}
    >
      <ReactionButton
        ariaLabel="This review is helpful"
        loading={isVoting === 'VOTE_UP'}
        icon={ThumbsUpIcon}
        count={votes[0]}
        onClick={() => handleVote(voteUp)}
      />
      <ReactionButton
        ariaLabel="This review is not helpful"
        loading={isVoting === 'VOTE_DOWN'}
        icon={ThumbsDownIcon}
        count={votes[1]}
        onClick={() => handleVote(voteDown)}
      />
    </Stack>
  );

  const footerMarkup =
    replyMarkup || votingMarkup ? (
      <Card.Section
        noBorder={replyMarkup === null}
        noTopPadding={replyMarkup === null}
        classNames={['wbtxReviewCard__footer']}
      >
        <Stack vertical spacing="tight">
          {replyMarkup}
          {votingMarkup}
        </Stack>
      </Card.Section>
    ) : null;

  return (
    <Card>
      <Card.Section classNames={['wbtxReviewCard__content']}>
        <Stack vertical>
          <Header
            author={author}
            title={title}
            rating={rating}
            verified={verified_purchase === 'VERIFIED'}
            source={source}
            publishDate={formatDate(created_at, locale, customDateFormat)}
            location={show_location && location ? location : undefined}
          />
          <Stack.Item fill classNames={['wbtxReviewCard__body']}>
            <Stack vertical>
              <Stack.Item classNames={['wbtxReviewCard__body-text']}>
                <Text size="small">{body}</Text>
              </Stack.Item>
              {(pros || cons) && (
                <Stack
                  vertical
                  spacing="tight"
                  classNames={['wbtxReviewCard__body-prosCons']}
                >
                  {prosMarkup}
                  {consMarkup}
                </Stack>
              )}
              {imagesMarkup}
            </Stack>
          </Stack.Item>
        </Stack>
      </Card.Section>
      {footerMarkup}
    </Card>
  );
}
